import { useEffect, useState } from 'react';

/**
 * Whether this visitor has asked for less motion.
 *
 * Read live rather than once at module load: it is a system setting people
 * change mid-session, usually because something on screen is bothering them.
 */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (event: MediaQueryListEvent): void => {
      setReduced(event.matches);
    };

    query.addEventListener('change', onChange);

    return () => {
      query.removeEventListener('change', onChange);
    };
  }, []);

  return reduced;
}

export interface Presence {
  /** Render the element at all. Stays true through the exit animation. */
  mounted: boolean;
  /** Apply the "open" styles. False for one frame on enter, and on exit. */
  entered: boolean;
}

/**
 * Keeps an element on screen long enough to animate away.
 *
 * A panel that returns `null` the moment it closes cannot have an exit
 * animation - there is nothing left to animate. This holds it mounted for the
 * length of the transition, and flips `entered` a frame after mounting so the
 * browser has an initial state to transition *from*.
 *
 * Falls back to instant when the visitor has asked for reduced motion.
 */
export function usePresence(open: boolean, durationMs = 220): Presence {
  const reducedMotion = usePrefersReducedMotion();
  const duration = reducedMotion ? 0 : durationMs;

  /* Only the *exit* needs to be remembered: it is the one moment the element
     has to outlive the flag that owns it. Being open is not state at all - it
     is the argument - so `mounted` below is derived rather than stored. An
     earlier version tracked mounting in its own state, which meant a dropped
     update could leave a panel that was open but never rendered. */
  const [closing, setClosing] = useState(false);
  const [entered, setEntered] = useState(open);
  const [previousOpen, setPreviousOpen] = useState(open);

  if (previousOpen !== open) {
    setPreviousOpen(open);

    if (open) {
      /* Re-opened, possibly part-way through closing: drop the pending exit. */
      setClosing(false);

      if (duration === 0) {
        setEntered(true);
      }
    } else if (duration === 0) {
      setEntered(false);
    } else {
      setClosing(true);
    }
  }

  const mounted = open || closing;

  /*
   * One effect owns the whole transition, both directions.
   *
   * Enter and exit used to be two effects sharing the same dependencies, so
   * they re-ran together on every change and each had a pending frame the
   * other could land on top of - which one won came down to ordering.
   */
  useEffect(() => {
    if (duration === 0) {
      return;
    }

    if (open) {
      /* Two frames, not one. The first lets the browser paint the closed
         position; the second flips to the open one. Setting both within a
         single frame means the browser only ever sees the final value, so it
         skips the transition rather than playing it. */
      let second = 0;

      const first = requestAnimationFrame(() => {
        second = requestAnimationFrame(() => {
          setEntered(true);
        });
      });

      return () => {
        cancelAnimationFrame(first);
        cancelAnimationFrame(second);
      };
    }

    /* Exit: play the transition out, then take it off the page. */
    const frame = requestAnimationFrame(() => {
      setEntered(false);
    });

    const timer = setTimeout(() => {
      setClosing(false);
    }, duration);

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
    };
  }, [open, duration]);

  return { mounted, entered };
}
